<template>
  <div class="early-year-report">
    <div class="no-print control-bar">
      <div class="d-flex align-items-center">
        <h4 class="mb-0">{{ reportDisplayName }}</h4>
        <b-badge
          v-if="!loading && reportStudents.length"
          variant="light-success"
          class="ml-2"
        >
          {{ reportStudents.length }} {{ $t("Student(s)") }}
        </b-badge>
      </div>
      <div class="action-buttons">
        <button class="btn-modern btn-print" @click="print">
          <feather-icon icon="PrinterIcon" size="18" class="mr-50" />
          <span>{{ $t("Print Report") }}</span>
        </button>
        <button class="btn-modern btn-close" @click="close">
          <feather-icon icon="XIcon" size="18" class="mr-50" />
          <span>{{ $t("Close") }}</span>
        </button>
      </div>
    </div>

    <div v-if="loading" class="state-panel state-panel--loading">
      <div class="state-panel__spinner-wrap">
        <b-spinner variant="primary" class="state-panel__spinner" />
      </div>
      <h5 class="state-panel__title">{{ $t("Loading Report") }}</h5>
      <p class="state-panel__text">
        {{ loadingReportMessage }}
      </p>
    </div>

    <div v-else-if="error" class="state-panel state-panel--error">
      <div class="state-panel__icon-wrap">
        <feather-icon icon="AlertCircleIcon" size="36" class="text-danger" />
      </div>
      <h5 class="state-panel__title text-danger">
        {{ $t("Unable to load report") }}
      </h5>
      <p class="state-panel__text">{{ error }}</p>
      <button class="btn-modern btn-print mt-2" @click="loadReportData">
        <feather-icon icon="RefreshCwIcon" size="16" class="mr-50" />
        <span>{{ $t("Retry") }}</span>
      </button>
    </div>

    <div v-else class="report-stack">
      <div
        v-for="(student, studentIndex) in displayStudents"
        :key="'eyr-' + (student.sID || studentIndex)"
        class="report-sheet"
        :class="{ 'report-sheet--junior': isJuniorLineLayout }"
      >
        <div class="report-sheet__frame">
          <div class="report-sheet__body">
            <!-- Header -->
            <header class="letterhead">
              <div class="letterhead__logo-wrap">
                <img
                  v-if="schoolLogo"
                  :src="schoolLogo"
                  alt="School logo"
                  class="letterhead__logo"
                />
                <div v-else class="letterhead__logo-fallback">
                  <feather-icon icon="AwardIcon" size="44" />
                </div>
              </div>

              <h1 class="letterhead__school">{{ campusName }}</h1>
              <p class="letterhead__tagline">
                {{ $t("Pre-Junior to O-Level, Matric & Intermediate") }}
              </p>
              <p class="letterhead__note">
                {{
                  $t("(P-6061) Registered & Recognised by the Cambridge Board")
                }}
              </p>
              <p class="letterhead__exam">{{ examSessionTitle }}</p>
            </header>

            <!-- Student Info Table -->
            <table class="student-info-table">
              <tbody>
                <tr>
                  <td class="student-info-table__cell">
                    <span class="student-info-table__label"
                      >{{ $t("Student Name") }}:</span
                    >
                    <span class="student-info-table__value">{{
                      val(student.name)
                    }}</span>
                  </td>
                  <td class="student-info-table__cell">
                    <span class="student-info-table__label"
                      >{{ $t("CID") }}:</span
                    >
                    <span class="student-info-table__value">{{
                      val(student.grNo || student.sID)
                    }}</span>
                  </td>
                  <td class="student-info-table__photo" rowspan="3">
                    <div class="student-info-table__photo-frame">
                      <img
                        v-if="student.picture"
                        :src="student.picture"
                        alt="Student"
                        class="student-info-table__img"
                        loading="eager"
                        decoding="sync"
                      />
                      <div v-else class="student-info-table__photo-placeholder">
                        <feather-icon icon="UserIcon" size="64" />
                      </div>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td class="student-info-table__cell">
                    <span class="student-info-table__label"
                      >{{ $t("Father Name") }}:</span
                    >
                    <span class="student-info-table__value">{{
                      val(student.fatherName)
                    }}</span>
                  </td>
                  <td class="student-info-table__cell">
                    <span class="student-info-table__label"
                      >{{ $t("Attendance") }}:</span
                    >
                    <span class="student-info-table__value">{{
                      attendanceDisplay(student)
                    }}</span>
                  </td>
                </tr>
                <tr>
                  <td class="student-info-table__cell" colspan="2">
                    <span class="student-info-table__label"
                      >{{ $t("Class & Section") }}:</span
                    >
                    <span class="student-info-table__value">{{
                      classSectionDisplay
                    }}</span>
                  </td>
                </tr>
              </tbody>
            </table>

            <!-- Scoring Legend -->
            <section class="scoring-legend">
              <table class="scoring-legend__table">
                <thead>
                  <tr>
                    <th colspan="2" class="scoring-legend__head">
                      <span class="scoring-legend__head-title">
                        {{ $t("ACADEMIC ACHIEVEMENT") }}
                      </span>
                      <span class="scoring-legend__head-sub">
                        {{ $t("Scoring Legend") }}
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td colspan="2" class="scoring-legend__intro-cell">
                      {{
                        $t(
                          "A student can exhibit achievement of the learning standards according to the following four-point scale:"
                        )
                      }}
                    </td>
                  </tr>
                  <tr
                    v-for="(row, rowIndex) in scoringLegendRows"
                    :key="'legend-row-' + rowIndex"
                  >
                    <td
                      v-for="item in row"
                      :key="'legend-' + item.code"
                      class="scoring-legend__cell"
                    >
                      <div class="scoring-legend__item">
                        <span
                          class="scoring-legend__badge"
                          :class="
                            'scoring-legend__badge--' + item.code.toLowerCase()
                          "
                        >
                          {{ item.code }}
                        </span>
                        <div class="scoring-legend__body">
                          <div class="scoring-legend__label">
                            {{ item.label }}
                          </div>
                          <p class="scoring-legend__desc">
                            {{ item.description }}
                          </p>
                        </div>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </section>

            <!-- Pre-Junior only: Vision / Mission under scoring legend -->
            <section v-if="!isJuniorLineLayout" class="vision-mission">
              <div class="vision-mission__col">
                <div class="vision-mission__title">
                  {{ $t("OUR VISION") }}
                </div>
                <div class="vision-mission__subtitle">
                  {{ $t("TO EMPOWER AND PROMOTE") }}
                </div>
                <ul class="vision-mission__list">
                  <li>{{ $t("Power of Thinking") }}</li>
                  <li>{{ $t("Learning and Doing") }}</li>
                  <li>{{ $t("Enlighten Consciousness") }}</li>
                  <li>{{ $t("Help Country for Future Greatness") }}</li>
                </ul>
              </div>
              <div class="vision-mission__col">
                <div class="vision-mission__title">
                  {{ $t("OUR MISSION") }}
                </div>
                <div class="vision-mission__subtitle">
                  {{ $t("TO IGNITE AND NURTURE") }}
                </div>
                <ul class="vision-mission__list">
                  <li>{{ $t("Latent Potential") }}</li>
                  <li>{{ $t("Enterprising Ability") }}</li>
                  <li>{{ $t("Critical Thinking") }}</li>
                  <li>{{ $t("Global Perspective") }}</li>
                  <li>{{ $t("Good Values") }}</li>
                  <li>{{ $t("Intellectual Independence") }}</li>
                  <li>{{ $t("Inspiring Excellence") }}</li>
                </ul>
              </div>
            </section>

            <!-- Assessment -->
            <section class="assessment-section">
              <!-- JR / Junior: 2 balanced columns (tall + short subjects pack evenly) -->
              <template v-if="isJuniorLineLayout">
                <div
                  v-if="student.assessment.allCategories.length"
                  class="junior-subjects"
                >
                  <div
                    v-for="(column, colIndex) in balanceCategoriesIntoColumns(
                      student.assessment.allCategories
                    )"
                    :key="'jr-col-' + student.sID + '-' + colIndex"
                    class="junior-subjects__col"
                    :class="{
                      'junior-subjects__col--right': colIndex === 1,
                    }"
                  >
                    <div
                      v-for="(category, catIndex) in column"
                      :key="'junior-' + student.sID + '-' + category.key"
                      class="subject-cell"
                      :class="{
                        'subject-cell--col-last':
                          catIndex === column.length - 1,
                      }"
                    >
                      <table class="rc-table rc-table--subject">
                        <colgroup>
                          <col />
                          <col
                            v-if="category.hasUrduRemarks"
                            class="rc-col-mark"
                          />
                          <col
                            v-if="category.hasUrduRemarks"
                            class="rc-col-mark"
                          />
                          <col v-else class="rc-col-mark" />
                        </colgroup>
                        <thead>
                          <tr>
                            <th
                              :colspan="category.hasUrduRemarks ? 3 : 2"
                              class="rc-table__skill-title"
                            >
                              {{ category.title }}
                              <span
                                v-if="category.hasUrduRemarks"
                                class="rc-table__title-hint"
                              >
                                (E / U)</span
                              >
                            </th>
                          </tr>
                          <tr
                            v-if="category.hasUrduRemarks"
                            class="rc-table__eu-head"
                          >
                            <th class="rc-table__criteria-head"></th>
                            <th class="rc-table__col-eu">E</th>
                            <th class="rc-table__col-eu">U</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr
                            v-for="(item, idx) in category.items"
                            :key="category.key + '-jr-' + idx"
                          >
                            <td class="rc-table__criteria">
                              {{ item.optionName }}
                            </td>
                            <template v-if="category.hasUrduRemarks">
                              <td class="rc-table__mark">
                                <span class="rc-grade">{{
                                  formatMark(item.rating)
                                }}</span>
                              </td>
                              <td class="rc-table__mark">
                                <span
                                  v-if="item.urduRemarks"
                                  class="rc-grade"
                                  >{{ formatMark(item.urduRating) }}</span
                                >
                              </td>
                            </template>
                            <td v-else class="rc-table__mark">
                              <span class="rc-grade">{{
                                formatMark(item.rating)
                              }}</span>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </template>

              <!-- Pre-Junior: box grid layout -->
              <template v-else>
                <div class="lang-banner">
                  {{ $t("LANGUAGE (ENGLISH / URDU)") }}
                </div>

                <div
                  v-if="student.assessment.languageSkills.length"
                  class="lang-skills"
                  :class="{
                    'lang-skills--odd':
                      student.assessment.languageSkills.length % 2 === 1,
                  }"
                >
                  <div
                    v-for="(skill, skillIndex) in alignPairedGridCategories(
                      student.assessment.languageSkills
                    )"
                    :key="'lang-skill-' + student.sID + '-' + skill.key"
                    class="subject-cell"
                    :class="{
                      'subject-cell--full': isLastOddGridItem(
                        skillIndex,
                        student.assessment.languageSkills.length
                      ),
                    }"
                  >
                    <table class="rc-table rc-table--skill">
                      <colgroup>
                        <col />
                        <col v-if="skill.hasUrduRemarks" class="rc-col-mark" />
                        <col v-if="skill.hasUrduRemarks" class="rc-col-mark" />
                        <col v-else class="rc-col-mark" />
                      </colgroup>
                      <thead>
                        <tr>
                          <th
                            :colspan="skill.hasUrduRemarks ? 3 : 2"
                            class="rc-table__skill-title"
                          >
                            {{ skill.title }}
                          </th>
                        </tr>
                        <tr
                          v-if="skill.hasUrduRemarks"
                          class="rc-table__eu-head"
                        >
                          <th class="rc-table__criteria-head"></th>
                          <th class="rc-table__col-eu">E</th>
                          <th class="rc-table__col-eu">U</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="(item, idx) in skill.items"
                          :key="skill.key + '-row-' + idx"
                          :class="{ 'rc-table__row--pad': item.isPad }"
                        >
                          <td class="rc-table__criteria">
                            {{ item.optionName }}
                          </td>
                          <template v-if="skill.hasUrduRemarks">
                            <td class="rc-table__mark">
                              <span class="rc-grade">{{
                                formatMark(item.rating)
                              }}</span>
                            </td>
                            <td class="rc-table__mark">
                              <span v-if="item.urduRemarks" class="rc-grade">{{
                                formatMark(item.urduRating)
                              }}</span>
                            </td>
                          </template>
                          <td v-else class="rc-table__mark">
                            <span class="rc-grade">{{
                              formatMark(item.rating)
                            }}</span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <div
                  v-if="student.assessment.coreSubjects.length"
                  class="subject-skills"
                  :class="{
                    'subject-skills--odd':
                      student.assessment.coreSubjects.length % 2 === 1,
                  }"
                >
                  <div
                    v-for="(subject, subjectIndex) in alignPairedGridCategories(
                      student.assessment.coreSubjects
                    )"
                    :key="'subject-' + student.sID + '-' + subject.key"
                    class="subject-cell"
                    :class="{
                      'subject-cell--full': isLastOddGridItem(
                        subjectIndex,
                        student.assessment.coreSubjects.length
                      ),
                    }"
                  >
                    <table class="rc-table rc-table--subject">
                      <colgroup>
                        <col />
                        <col
                          v-if="subject.hasUrduRemarks"
                          class="rc-col-mark"
                        />
                        <col
                          v-if="subject.hasUrduRemarks"
                          class="rc-col-mark"
                        />
                        <col v-else class="rc-col-mark" />
                      </colgroup>
                      <thead>
                        <tr>
                          <th
                            :colspan="subject.hasUrduRemarks ? 3 : 2"
                            class="rc-table__skill-title"
                          >
                            {{ subject.title }}
                          </th>
                        </tr>
                        <tr
                          v-if="subject.hasUrduRemarks"
                          class="rc-table__eu-head"
                        >
                          <th class="rc-table__criteria-head"></th>
                          <th class="rc-table__col-eu">E</th>
                          <th class="rc-table__col-eu">U</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="(item, idx) in subject.items"
                          :key="subject.key + '-row-' + idx"
                          :class="{ 'rc-table__row--pad': item.isPad }"
                        >
                          <td class="rc-table__criteria">
                            {{ item.optionName }}
                          </td>
                          <template v-if="subject.hasUrduRemarks">
                            <td class="rc-table__mark">
                              <span class="rc-grade">{{
                                formatMark(item.rating)
                              }}</span>
                            </td>
                            <td class="rc-table__mark">
                              <span v-if="item.urduRemarks" class="rc-grade">{{
                                formatMark(item.urduRating)
                              }}</span>
                            </td>
                          </template>
                          <td v-else class="rc-table__mark">
                            <span class="rc-grade">{{
                              formatMark(item.rating)
                            }}</span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </template>
            </section>

            <!-- Class Teacher Comments -->
            <section class="comments-section">
              <div class="comments-section__title">
                {{ $t("CLASS TEACHER'S COMMENTS") }}
              </div>
              <div class="comments-section__body">
                <div class="comments-section__rules" aria-hidden="true">
                  <div
                    v-for="lineIndex in 4"
                    :key="
                      'comment-rule-' +
                      (student.sID || studentIndex) +
                      '-' +
                      lineIndex
                    "
                    class="comments-section__rule"
                  ></div>
                </div>
                <p class="comments-section__text">
                  {{ student.teacherComment }}
                </p>
              </div>
            </section>

            <!-- Signatures -->
            <footer class="signatures signatures--junior">
              <div class="signatures__item">
                <div class="signatures__line"></div>
                <div class="signatures__label">{{ $t("Class Teacher") }}</div>
              </div>
              <div class="signatures__item">
                <div class="signatures__line"></div>
                <div class="signatures__label">{{ $t("Principal") }}</div>
              </div>
              <div class="signatures__item signatures__item--date">
                <div class="signatures__date">{{ reportDate }}</div>
                <div class="signatures__line"></div>
                <div class="signatures__label">{{ $t("Date") }}</div>
              </div>
            </footer>
          </div>
        </div>
      </div>

      <div v-if="!displayStudents.length" class="state-panel">
        <h5 class="font-weight-bold mb-1">
          {{ $t("No student records found") }}
        </h5>
        <p class="text-muted mb-0">
          {{ $t("Try different filters on the print result page.") }}
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { BButton, BSpinner, BBadge } from "bootstrap-vue";
import axios from "axios";

const LANGUAGE_CATEGORIES = ["LISTENING", "SPEAKING", "READING", "WRITING"];

/* Pre-Junior IV only: English box + its Urdu box sit on the same row,
   Writing row first and Reading row below it. */
const PRE_JUNIOR_FOUR_LANGUAGE_ORDER = [
  "LISTENING",
  "SPEAKING",
  "WRITING",
  "READING",
];

export default {
  name: "EarlyYearReport",
  components: {
    BButton,
    BSpinner,
    BBadge,
  },
  data() {
    return {
      loading: false,
      error: null,
      reportStudents: [],
      pictureMap: {},
      campusName: "MY SKOOL",
      campusBranch: "",
      schoolLogo: null,
      reportMeta: {
        className: "",
        sectionName: "",
        examTypeName: "",
        sessionName: "",
      },
      savedResultPrintDate: "",
      scoringLegend: [
        {
          code: "E",
          label: "Exceeding expectation",
          description:
            "The student consistently and independently demonstrates a high understanding of grade level skills and concepts and can apply this learning to new situations.",
        },
        {
          code: "A",
          label: "Approaching expectation",
          description:
            "The student is beginning to demonstrate an understanding of grade level skills and concepts with teacher support.",
        },
        {
          code: "M",
          label: "Meeting expectation",
          description:
            "The student consistently demonstrates an understanding of grade level skills and concepts.",
        },
        {
          code: "N",
          label: "Not yet meeting expectation",
          description:
            "The student demonstrates a limited understanding of grade level skills and concepts with considerable teacher support.",
        },
      ],
    };
  },
  computed: {
    scoringLegendRows() {
      const rows = [];
      const items = this.scoringLegend || [];
      for (let i = 0; i < items.length; i += 2) {
        rows.push(items.slice(i, i + 2));
      }
      return rows;
    },
    isPreJuniorLayout() {
      const normalized = String(this.reportMeta.className || "")
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, "");
      if (!normalized) return false;
      return (
        normalized.includes("PJR") ||
        normalized.includes("PREJNR") ||
        normalized.includes("PREJUNIOR") ||
        normalized.includes("PREJR")
      );
    },
    isJuniorLineLayout() {
      const normalized = String(this.reportMeta.className || "")
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, "");
      if (!normalized) return false;
      if (this.isPreJuniorLayout) return false;

      let suffix = "";
      if (normalized.startsWith("JUNIOR")) {
        suffix = normalized.slice(6);
      } else if (normalized.startsWith("JR")) {
        suffix = normalized.slice(2);
      } else if (normalized.includes("JUNIOR")) {
        suffix = normalized.slice(normalized.indexOf("JUNIOR") + 6);
      } else {
        return false;
      }

      // Only Junior / Junior I / Junior II are early-year junior layouts
      if (/^(III|IV|VI{0,3}|IX|X|[3-9]|1[0-2])[A-Z]*$/.test(suffix)) {
        return false;
      }
      return /^(?:I{1,2}|1|2)?[A-Z]?$/.test(suffix);
    },
    /** Pre-Junior IV only — Reading / Writing exist in both English and Urdu versions. */
    isPreJuniorFourLayout() {
      if (!this.isPreJuniorLayout) return false;
      const normalized = String(this.reportMeta.className || "")
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, "");
      let suffix = normalized;
      ["PREJUNIOR", "PREJNR", "PREJR", "PJR"].some((prefix) => {
        if (suffix.startsWith(prefix)) {
          suffix = suffix.slice(prefix.length);
          return true;
        }
        return false;
      });
      return /^(?:IV|4)[A-Z]?$/.test(suffix);
    },
    reportDisplayName() {
      if (this.isJuniorLineLayout) return this.$t("Junior Report");
      if (this.isPreJuniorLayout) return this.$t("Pre Junior Report");
      return this.$t("Pre Junior Report");
    },
    loadingReportMessage() {
      return `${this.$t("Please wait while we prepare the")} ${String(
        this.reportDisplayName || ""
      ).toLowerCase()}…`;
    },
    gradeTitle() {
      const cls = this.reportMeta.className || "III";
      const sec = this.reportMeta.sectionName;
      return sec ? `${cls} - ${sec}` : cls;
    },
    classSectionDisplay() {
      const cls = this.reportMeta.className || "";
      const sec = this.reportMeta.sectionName || "";
      return [cls, sec].filter(Boolean).join(" ") || "—";
    },
    examSessionTitle() {
      const exam =
        this.reportMeta.examTypeName || this.$t("1st Bi-Monthly Examination");
      const session = this.reportMeta.sessionName
        ? ` Session ${this.reportMeta.sessionName}`
        : "";
      return `${exam}${session}`;
    },
    reportDate() {
      return this.formatReportPrintDate(this.savedResultPrintDate);
    },
    displayStudents() {
      return this.reportStudents.map((student) => {
        const attendance = this.resolveAttendance(student);
        return {
          sID: student.sID,
          name: student.name,
          fatherName: student.fatherName,
          grNo: student.grNo,
          dateOfBirth: student.dateOfBirth || student.dob || "—",
          dateOfAdmission:
            student.dateOfAdmission || student.admissionDate || "—",
          totalWorkingDays: attendance.total,
          daysAttended: attendance.obtained,
          picture: this.normalizePictureUrl(
            student.picture ||
              student.photo ||
              student.image ||
              this.pictureMap[String(student.sID)] ||
              null
          ),
          teacherComment: this.resolveTeacherComment(student),
          assessment: this.buildStudentAssessment(student),
        };
      });
    },
  },
  created() {
    this.initMetaFromQuery();
    this.loadCampusInfo();
    this.loadResultPrintDate();
    this.loadReportData();
  },
  methods: {
    initMetaFromQuery() {
      const q = this.$route.query;
      this.reportMeta = {
        className: q.clsName || "III",
        sectionName: q.secName || "",
        examTypeName: q.examTypeName || "",
        sessionName: q.sessionName || "",
      };
      if (q.resultDate) {
        this.savedResultPrintDate = String(q.resultDate);
      }
    },

    formatReportPrintDate(raw) {
      if (!raw) return "—";
      let part = String(raw).trim();
      if (part.indexOf(" to ") > -1) {
        part = part.split(" to ").pop().trim();
      }
      const iso = part.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (iso) {
        const months = [
          "January",
          "February",
          "March",
          "April",
          "May",
          "June",
          "July",
          "August",
          "September",
          "October",
          "November",
          "December",
        ];
        const day = parseInt(iso[3], 10);
        const month = months[parseInt(iso[2], 10) - 1];
        const ordinals = ["th", "st", "nd", "rd"];
        const v = day % 100;
        const suffix =
          ordinals[(v - 20) % 10] || ordinals[v] || ordinals[0];
        return `${day}${suffix} ${month}, ${iso[1]}`;
      }
      return part;
    },

    async loadResultPrintDate() {
      const db = this.$route.query.db || this.$store.state.userData.db;
      const cID = this.$route.query.cID || this.$store.state.userData.cId;
      const token = this.$store.state.userData.token;
      if (!db || !cID || !token) return;

      try {
        const response = await axios.post(
          `${this.$store.state.domain}Settings/getSettings?db=${db}&cID=${cID}`,
          ["result_print_date"],
          {
            headers: {
              Authorization: `Bearer ${token}`,
              accept: "application/json",
            },
          }
        );
        const payload = response.data;
        let rows = [];
        if (
          payload &&
          payload.status === "success" &&
          Array.isArray(payload.data)
        ) {
          rows = payload.data;
        } else if (Array.isArray(payload)) {
          rows = payload;
        } else if (payload && Array.isArray(payload.data)) {
          rows = payload.data;
        }
        if (!rows.length) return;
        const currentCampusId = String(cID);
        const row =
          rows.find(
            (el) =>
              el.key === "result_print_date" &&
              String(el.campusID) === currentCampusId
          ) || rows.find((el) => el.key === "result_print_date");
        if (row && row.value) {
          this.savedResultPrintDate = row.value;
        }
      } catch (e) {
        console.warn("EarlyYearReport: could not load result print date", e);
      }
    },

    async loadCampusInfo() {
      const db = this.$route.query.db || this.$store.state.userData.db;
      const cID = this.$route.query.cID || this.$store.state.userData.cId;
      const token = this.$store.state.userData.token;

      try {
        const response = await axios.get(
          "https://apiv26.myskool.app/api/campuses",
          {
            params: { db, cId: cID },
            headers: {
              Authorization: `Bearer ${token}`,
              accept: "application/json",
            },
          }
        );

        if (
          response.data &&
          response.data.status === "success" &&
          Array.isArray(response.data.data)
        ) {
          const match = response.data.data.find(
            (c) => String(c.id) === String(cID)
          );
          if (match) {
            this.campusName = match.name || this.campusName;
            this.campusBranch = match.branch || "";
            this.schoolLogo = match.logo || null;
          }
        }
      } catch (e) {
        const branches = this.$store.state.branches || [];
        const currentBranch =
          branches.find((b) => String(b.id) === String(cID)) ||
          this.$store.state.currentBranch;
        if (currentBranch) {
          this.campusName =
            currentBranch.organizationName ||
            currentBranch.name ||
            currentBranch.branch ||
            this.campusName;
          this.campusBranch = currentBranch.branch || "";
          this.schoolLogo = currentBranch.logo || this.schoolLogo;
        }
      }
    },

    async loadReportData() {
      const q = this.$route.query;
      const db = q.db || this.$store.state.userData.db;
      const cID = q.cID || this.$store.state.userData.cId;
      const classID = q.classID;
      const sectionID = q.sectionID;
      const examTypeID = q.examTypeID;
      const sessionID = q.sessionID;

      if (!classID || !sectionID || !examTypeID || !sessionID) {
        this.error = this.$t(
          "Missing report filters. Please open this report from Print Result."
        );
        return;
      }

      this.loading = true;
      this.error = null;

      try {
        const response = await axios.get(
          `${this.$store.state.domain}TabulationSheetMarks/GetStudentWiseByFilters`,
          {
            params: {
              db,
              cID,
              classID,
              sectionID,
              examTypeID,
              sessionID,
            },
            headers: {
              Authorization: `Bearer ${this.$store.state.userData.token}`,
              accept: "application/json",
            },
          }
        );

        const payload = response.data;
        let rows = [];
        if (
          payload &&
          payload.status === "success" &&
          Array.isArray(payload.data)
        ) {
          rows = payload.data;
        } else if (Array.isArray(payload)) {
          rows = payload;
        } else if (payload && Array.isArray(payload.data)) {
          rows = payload.data;
        }

        const sIDs = String(q.sIDs || "")
          .split(",")
          .map((id) => id.trim())
          .filter(Boolean);

        if (sIDs.length) {
          rows = rows.filter((row) => sIDs.includes(String(row.sID)));
        }

        this.reportStudents = rows;
        this.fetchStudentPictures();
      } catch (e) {
        console.error("Early year report load error:", e);
        this.error =
          (e.response && e.response.data && e.response.data.message) ||
          this.$t("Failed to load report.");
      } finally {
        this.loading = false;
      }
    },

    async fetchStudentPictures() {
      try {
        const q = this.$route.query;
        const db = q.db || this.$store.state.userData.db;
        const cID = q.cID || this.$store.state.userData.cId;
        const classID = q.classID || 0;
        const sectionID = q.sectionID || 0;

        const res = await axios.get(
          `${this.$store.state.domain}students/Filterdata?cls=${classID}&sec=${sectionID}&status=present&db=${db}&cId=${cID}&page=1`,
          {
            headers: {
              Authorization: `Bearer ${this.$store.state.userData.token}`,
            },
          }
        );

        let list = [];
        if (res.data && res.data.status === "success") {
          list = Array.isArray(res.data.data)
            ? res.data.data
            : (res.data.data && res.data.data.data) || [];
        } else if (Array.isArray(res.data)) {
          list = res.data;
        }

        const newMap = {};
        list.forEach((s) => {
          newMap[String(s.id || s.sID)] = this.normalizePictureUrl(
            s.picture || s.photo || null
          );
        });
        this.pictureMap = newMap;
      } catch (e) {
        console.warn("Student pictures fetch failed:", e);
      }
    },

    normalizePictureUrl(url) {
      if (!url) return null;
      const src = String(url).trim();
      if (!src || src.startsWith("data:")) return src || null;
      try {
        const parsed = new URL(src, window.location.origin);
        // Drop tiny thumbnail size params so print/download uses a sharper source
        ["w", "width", "h", "height", "size", "thumb", "thumbnail"].forEach(
          (key) => {
            const val = parsed.searchParams.get(key);
            if (val == null) return;
            const num = Number(val);
            if (!Number.isFinite(num) || num > 0) {
              parsed.searchParams.delete(key);
            }
          }
        );
        return parsed.toString();
      } catch (e) {
        return src;
      }
    },

    formatAttendanceNumber(value) {
      if (value === null || value === undefined || value === "") return "";
      const num = Number(value);
      if (Number.isNaN(num)) return String(value).trim();
      return Number.isInteger(num) ? String(num) : String(num);
    },

    resolveAttendance(student) {
      const att = student && student.attendance;
      if (att && typeof att === "object" && !Array.isArray(att)) {
        return {
          obtained: this.formatAttendanceNumber(
            att.obtained ?? att.originalObtained ?? ""
          ),
          total: this.formatAttendanceNumber(att.total ?? ""),
        };
      }

      const obtainedFallback =
        student.daysAttended ??
        (typeof att === "string" || typeof att === "number" ? att : "") ??
        "";
      const totalFallback =
        student.totalWorkingDays ?? student.workingDays ?? "";

      return {
        obtained: this.formatAttendanceNumber(obtainedFallback),
        total: this.formatAttendanceNumber(totalFallback),
      };
    },

    resolveTeacherComment(student) {
      const existing = student.teacherComments || student.comments;
      if (Array.isArray(existing) && existing.length) {
        return existing
          .map((line) => String(line || "").trim())
          .filter(Boolean)
          .join(" ");
      }
      if (typeof existing === "string" && existing.trim()) {
        return existing.trim();
      }

      const remarks = student.generalRemarks || {};
      return String(
        remarks.grade ||
          remarks.meta_sub_grade ||
          remarks.remarks ||
          student.teacherComment ||
          ""
      ).trim();
    },

    attendanceDisplay(student) {
      const attended = student.daysAttended;
      const working = student.totalWorkingDays;
      if (
        attended !== null &&
        attended !== undefined &&
        attended !== "" &&
        typeof attended === "object"
      ) {
        return this.formatAttendanceNumber(
          attended.obtained ?? attended.originalObtained ?? ""
        );
      }
      if (
        attended !== "" &&
        attended != null &&
        working !== "" &&
        working != null
      ) {
        return `${attended} / ${working}`;
      }
      if (attended !== "" && attended != null) return String(attended);
      if (working !== "" && working != null) return String(working);
      return "";
    },

    val(value) {
      if (value === null || value === undefined || value === "") return "—";
      return String(value);
    },

    formatMark(value) {
      if (value === null || value === undefined || value === "") return "";
      return String(value).trim();
    },

    isLastOddGridItem(index, total) {
      const len = Number(total) || 0;
      return len % 2 === 1 && index === len - 1;
    },

    /** Pad side-by-side Pre-Junior grid pairs so row lines align (e.g. READING vs WRITING). */
    alignPairedGridCategories(categories) {
      const list = (Array.isArray(categories) ? categories : []).map(
        (category) => ({
          ...category,
          items: Array.isArray(category.items)
            ? category.items.map((item) => ({ ...item }))
            : [],
        })
      );

      const emptyRow = () => ({
        optionName: "",
        rating: "",
        urduRating: "",
        urduRemarks: false,
        isPad: true,
      });

      for (let i = 0; i < list.length; i += 2) {
        const left = list[i];
        const right = list[i + 1];
        if (!right) continue;

        const maxLen = Math.max(left.items.length, right.items.length);
        while (left.items.length < maxLen) left.items.push(emptyRow());
        while (right.items.length < maxLen) right.items.push(emptyRow());
      }

      return list;
    },

    /**
     * Pack subjects into 2 columns by approximate height (row count)
     * so a short subject sits under another instead of leaving a tall gap.
     */
    balanceCategoriesIntoColumns(categories) {
      const list = Array.isArray(categories) ? categories.slice() : [];
      if (!list.length) return [[], []];

      const weightOf = (category) => {
        const rows = (category.items && category.items.length) || 0;
        // title + optional E/U header + criteria rows
        return rows + (category.hasUrduRemarks ? 2 : 1);
      };

      // Place heaviest first so packing stays more even
      const ordered = list.slice().sort((a, b) => weightOf(b) - weightOf(a));

      const columns = [[], []];
      const weights = [0, 0];

      ordered.forEach((category) => {
        const target = weights[0] <= weights[1] ? 0 : 1;
        columns[target].push(category);
        weights[target] += weightOf(category);
      });

      // Keep a readable order within each column (original sortIndex / title)
      const byOriginal = (a, b) => {
        const si = (Number(a.sortIndex) || 999) - (Number(b.sortIndex) || 999);
        if (si !== 0) return si;
        return String(a.title || "").localeCompare(String(b.title || ""));
      };
      columns[0].sort(byOriginal);
      columns[1].sort(byOriginal);

      return columns;
    },

    /**
     * Pre-Junior IV: keep the language boxes in this order —
     * LISTENING, SPEAKING, then WRITING (English + Urdu side by side),
     * then READING (English + Urdu side by side).
     */
    buildPreJuniorFourLanguageSkills(categories) {
      const groups = {};

      categories.forEach((category) => {
        const title = String(category.title || "").toUpperCase();
        const base = title
          .replace(/[^A-Z0-9 ]/g, " ")
          .replace(/\b(ENGLISH|URDU)\b/g, " ")
          .replace(/\s+/g, " ")
          .trim();
        if (!PRE_JUNIOR_FOUR_LANGUAGE_ORDER.includes(base)) return;
        if (!groups[base]) groups[base] = { english: [], urdu: [] };
        if (/\bURDU\b/.test(title)) {
          groups[base].urdu.push(category);
        } else {
          groups[base].english.push(category);
        }
      });

      const byIndex = (a, b) => a.sortIndex - b.sortIndex;

      return PRE_JUNIOR_FOUR_LANGUAGE_ORDER.reduce((ordered, base) => {
        const group = groups[base];
        if (!group) return ordered;
        // English box first, its Urdu version next to it on the same row
        ordered.push(...group.english.sort(byIndex));
        ordered.push(...group.urdu.sort(byIndex));
        return ordered;
      }, []);
    },

    buildStudentAssessment(student) {
      const allMarks = [];
      (student.records || []).forEach((record) => {
        (record.marksData || []).forEach((item) => allMarks.push(item));
      });

      const categoryMap = {};
      allMarks.forEach((item) => {
        const title = String(item.categoryName || "OTHER")
          .trim()
          .toUpperCase();
        if (!categoryMap[title]) {
          categoryMap[title] = {
            key: title,
            title,
            sortIndex: Number(item.categorySortIndex) || 999,
            items: [],
          };
        }
        categoryMap[title].items.push({
          optionName: item.optionName || "",
          rating: item.rating,
          urduRating: item.urduRating,
          urduRemarks: !!item.urduRemarks,
          optionSortIndex: Number(item.optionSortIndex) || 0,
        });
      });

      const categories = Object.values(categoryMap).map((category) => {
        category.items.sort((a, b) => a.optionSortIndex - b.optionSortIndex);
        category.hasUrduRemarks = category.items.some((row) => row.urduRemarks);
        return category;
      });

      const languageSkills = this.isPreJuniorFourLayout
        ? this.buildPreJuniorFourLanguageSkills(categories)
        : categories
            .filter((category) =>
              LANGUAGE_CATEGORIES.includes(category.title)
            )
            .sort((a, b) => a.sortIndex - b.sortIndex);

      const languageKeys = new Set(languageSkills.map((c) => c.key));
      const coreSubjects = categories
        .filter((category) => !languageKeys.has(category.key))
        .sort((a, b) => a.sortIndex - b.sortIndex);

      const allCategories = [...languageSkills, ...coreSubjects];

      return { languageSkills, coreSubjects, allCategories };
    },

    print() {
      window.print();
    },

    close() {
      window.close();
    },
  },
};
</script>

<style lang="scss" scoped>
$black: #000000;
$ink: #1a1a1a;
$paper: #ffffff;
$line: #b0b0b0;
$line-strong: #9a9a9a;
$report-font: "Segoe UI", "Helvetica Neue", Arial, sans-serif;

.early-year-report {
  background: #f0f2f5;
  min-height: 100vh;
  padding: 2.5rem;
  color: $ink;
  font-family: "Outfit", "Inter", $report-font;
}

.no-print.control-bar {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  padding: 1.25rem 2.5rem;
  margin-bottom: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 1rem;
  z-index: 1000;
  border: 1px solid rgba(255, 255, 255, 0.3);
  font-family: "Outfit", "Inter", sans-serif;

  h4 {
    color: #444;
    font-weight: 700;
    font-size: 1.25rem;
    letter-spacing: -0.5px;
    margin: 0;
  }
}

.action-buttons {
  display: flex;
  gap: 12px;
}

.btn-modern {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 24px;
  border-radius: 10px;
  font-weight: 700;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  border: none;
  outline: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);

  span {
    margin-left: 4px;
  }

  &:active {
    transform: scale(0.96);
  }
}

.btn-print {
  background: linear-gradient(135deg, #6c5ce7 0%, #a29bfe 100%);
  color: white;

  &:hover {
    background: linear-gradient(135deg, #5b4cc4 0%, #8c82eb 100%);
    box-shadow: 0 6px 20px rgba(108, 92, 231, 0.35);
    transform: translateY(-1px);
  }
}

.btn-close {
  background: white;
  color: #636e72;
  border: 1px solid #dfe6e9;

  &:hover {
    background: #fdfdfd;
    color: #e74c3c;
    border-color: #fab1a0;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.05);
    transform: translateY(-1px);
  }
}

.state-panel {
  text-align: center;
  padding: 2.75rem 2rem;
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 16px;
  max-width: 420px;
  margin: 4rem auto;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.08);
  font-family: "Outfit", "Inter", sans-serif;

  &__spinner-wrap {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 72px;
    height: 72px;
    border-radius: 50%;
    background: linear-gradient(
      135deg,
      rgba(108, 92, 231, 0.12) 0%,
      rgba(162, 155, 254, 0.18) 100%
    );
    margin-bottom: 1.1rem;
  }

  &__spinner {
    width: 2.4rem !important;
    height: 2.4rem !important;
    color: #6c5ce7 !important;
  }

  &__icon-wrap {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 72px;
    height: 72px;
    border-radius: 50%;
    background: rgba(231, 76, 60, 0.1);
    margin-bottom: 1rem;
  }

  &__title {
    margin: 0 0 0.35rem;
    font-size: 1.15rem;
    font-weight: 800;
    color: #2d3436;
    letter-spacing: -0.3px;
  }

  &__text {
    margin: 0;
    font-size: 0.95rem;
    color: #636e72;
    line-height: 1.5;
  }

  &--loading {
    .state-panel__title {
      color: #6c5ce7;
    }
  }
}

.report-stack {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.report-sheet {
  width: 210mm;
  min-height: auto;
  margin: 0 auto;
  page-break-after: always;

  &:last-child {
    page-break-after: auto;
  }

  &__frame {
    min-height: auto;
    height: auto;
    background: $paper;
    border: 1.5px solid $line-strong;
    outline: none;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
    box-sizing: border-box;
    overflow: visible;
  }

  &__body {
    min-height: auto;
    height: auto;
    display: flex;
    flex-direction: column;
    padding: 4mm 8mm 6mm;
    font-family: $report-font;
    font-size: 14px;
    box-sizing: border-box;
    overflow: visible;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }
}

/* ── Letterhead ── */
.letterhead {
  text-align: center;
  margin-bottom: 6px;
}

.letterhead__logo-wrap {
  margin-bottom: 4px;
}

.letterhead__logo {
  width: 92px;
  height: 92px;
  object-fit: contain;
}

.letterhead__logo-fallback {
  width: 92px;
  height: 92px;
  margin: 0 auto;
  border: 1px solid $line;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: $black;
}

.letterhead__school {
  font-size: 26px;
  font-weight: 700;
  text-transform: none;
  text-decoration: none;
  letter-spacing: 0.01em;
  margin: 0 0 4px;
  color: $black;
  line-height: 1.25;
  font-family: Georgia, "Times New Roman", serif;
}

.letterhead__tagline,
.letterhead__note {
  font-size: 15px;
  font-weight: 700;
  margin: 0;
  line-height: 1.4;
  color: $black;
  font-family: Georgia, "Times New Roman", serif;
}

.letterhead__exam {
  margin: 8px 0 0;
  font-size: 18px;
  font-weight: 700;
  color: $black;
  font-family: Georgia, "Times New Roman", serif;
}

/* ── Student info table ── */
.student-info-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  margin-bottom: 10px;
  font-family: Georgia, "Times New Roman", serif;

  td {
    border: 1px solid $line;
    vertical-align: middle;
    padding: 0;
  }
}

.student-info-table__cell {
  width: 40%;
  padding: 10px 12px !important;
  font-size: 15px;
  line-height: 1.4;
  color: $black;
}

.student-info-table__label {
  font-weight: 400;
}

.student-info-table__value {
  font-weight: 700;
  margin-left: 4px;
}

.student-info-table__photo {
  width: 132px;
  min-width: 132px;
  max-width: 132px;
  padding: 4px !important;
  background: #fff;
  vertical-align: middle;
  text-align: center;
}

.student-info-table__photo-frame {
  width: 120px;
  height: 140px;
  margin: 0 auto;
  overflow: hidden;
  position: relative;
  background: #f3f3f3;
  border: 1px solid $line;
  box-sizing: border-box;
}

.student-info-table__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center top;
  display: block;
  margin: 0;
  image-rendering: auto;
  -ms-interpolation-mode: bicubic;
}

.student-info-table__photo-placeholder {
  width: 100%;
  height: 100%;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #ececec;
  color: #9a9a9a;
}

/* ── Scoring Legend ── */
.scoring-legend {
  margin: 0 0 10px;
  page-break-inside: avoid;
}

.scoring-legend__table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  font-family: Georgia, "Times New Roman", serif;

  th,
  td {
    border: 1px solid $line;
    vertical-align: top;
  }
}

.scoring-legend__head {
  background: #f7f3f0;
  padding: 8px 12px;
  text-align: left;
}

.scoring-legend__head-title {
  display: block;
  font-size: 16px;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #8b1e2d;
  line-height: 1.2;
}

.scoring-legend__head-sub {
  display: block;
  margin-top: 2px;
  font-size: 15px;
  font-weight: 700;
  color: $black;
}

.scoring-legend__intro-cell {
  padding: 9px 12px;
  font-size: 14.5px;
  line-height: 1.4;
  color: #333;
  background: #fff;
}

.scoring-legend__cell {
  width: 50%;
  padding: 10px 12px;
  background: #fff;
}

.scoring-legend__item {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 12px;
}

.scoring-legend__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 4px;
  border: 1px solid transparent;
  font-size: 16px;
  font-weight: 800;
  flex-shrink: 0;
  line-height: 1;
  font-family: Georgia, "Times New Roman", serif;
}

.scoring-legend__badge--e {
  background: #c8e6c9;
  color: #1b5e20;
  border-color: #81c784;
}

.scoring-legend__badge--m {
  background: #bbdefb;
  color: #0d47a1;
  border-color: #64b5f6;
}

.scoring-legend__badge--a {
  background: #ffe0b2;
  color: #e65100;
  border-color: #ffb74d;
}

.scoring-legend__badge--n {
  background: #f8d4d4;
  color: #c62828;
  border-color: #e8a0a0;
}

.scoring-legend__body {
  min-width: 0;
}

.scoring-legend__label {
  font-size: 15px;
  font-weight: 700;
  color: $black;
  line-height: 1.3;
  margin-bottom: 4px;
}

.scoring-legend__desc {
  margin: 0;
  font-size: 13.5px;
  line-height: 1.4;
  color: #333;
  font-weight: 400;
}

/* Pre-Junior: Vision / Mission under scoring legend */
.vision-mission {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px 28px;
  margin: 0 0 8px;
  padding: 8px 12px 9px;
  background: #fff;
  border: 1px solid $line;
  box-sizing: border-box;
  page-break-inside: avoid;
  font-family: Georgia, "Times New Roman", serif;
}

.vision-mission__col {
  min-width: 0;
}

.vision-mission__title {
  display: inline-block;
  background: #1e3a6e;
  color: #fff;
  font-size: 14px;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 5px 12px;
  margin-bottom: 6px;
  line-height: 1.2;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}

.vision-mission__subtitle {
  font-size: 13.5px;
  font-weight: 800;
  text-transform: uppercase;
  color: $black;
  margin: 0 0 6px;
  line-height: 1.25;
  letter-spacing: 0.02em;
}

.vision-mission__list {
  margin: 0;
  padding: 0 0 0 18px;
  list-style: disc;

  li {
    font-size: 14px;
    font-weight: 500;
    color: $black;
    line-height: 1.35;
    margin: 0 0 3px;
  }
}

/* ── Assessment ── */
.assessment-section {
  flex: 0 1 auto;
  margin-bottom: 10px;
}

/* JR / Junior line-by-line — flow across pages (no forced blank gaps) */
.line-block {
  margin-bottom: 12px;
  page-break-inside: auto;
  break-inside: auto;
}

.line-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  margin: 0;
  font-family: Georgia, "Times New Roman", serif;
  page-break-inside: auto;
  break-inside: auto;

  th,
  td {
    border: 1px solid $line;
    vertical-align: middle;
    padding: 10px 12px;
    font-size: 16px;
    color: $black;
    background: #fff;
  }
}

.line-col-criteria {
  width: auto;
}

.line-col-mark {
  width: 88px;
}

.line-table__title {
  text-align: center;
  font-weight: 700;
  font-size: 17.5px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 11px 12px !important;
  page-break-after: avoid;
  break-after: avoid;
}

.line-table__title-hint {
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: none;
  font-size: 15px;
}

.line-table__criteria {
  text-align: left;
  font-weight: 700;
  line-height: 1.4;
  font-size: 16px;
}

.line-table__mark {
  text-align: center;
  width: 88px;
  font-weight: 700;
  font-size: 17px;
}

.grade-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  height: 28px;
  padding: 0 7px;
  border: 1px solid $line;
  background: #fff;
  font-size: 16px;
  font-weight: 800;
  line-height: 1;
  text-transform: uppercase;
}

.lang-banner {
  border: 1px solid $line;
  /* single separator line under heading — grid has no top border */
  border-bottom: 1px solid $line;
  text-align: center;
  font-weight: 700;
  font-size: 17px;
  letter-spacing: 0.05em;
  padding: 10px 12px;
  color: $black;
  box-sizing: border-box;
}

.lang-skills,
.subject-skills {
  display: grid;
  align-items: stretch;
  gap: 0;
  border: 1px solid $line;
  border-top: none; /* avoid double with banner / previous row */
  box-sizing: border-box;

  > .subject-cell {
    min-height: 100%;
    height: 100%;
    align-self: stretch;
    border-bottom: none; /* row lines come from table cells */

    .rc-table {
      width: 100%;
      height: 100%;
    }

    .rc-table tbody tr:last-child > * {
      border-bottom: 1px solid $line;
    }
  }
}

.lang-skills {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.subject-skills {
  grid-template-columns: repeat(2, minmax(0, 1fr));
  /* one shared line with lang-skills bottom — no double layer */
  margin-top: 0;
  border-top: none;
}

/* Junior: 2 balanced columns — web uses content height; print stretches columns */
.junior-subjects {
  display: flex;
  align-items: flex-start;
  border: 1px solid $line;
  box-sizing: border-box;
  page-break-inside: auto;
  break-inside: auto;
}

.junior-subjects__col {
  width: 50%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  box-sizing: border-box;
  border-right: 1px solid $line;
  background: #fff;

  &--right {
    border-right: none;
  }

  .subject-cell {
    width: 100%;
    display: block;
    flex: 0 0 auto; /* content height only — row lines stay on real rows */
    border: none;
    border-right: none;
    border-bottom: 1px solid $line; /* always close each subject box */
    page-break-inside: auto;
    break-inside: auto;
    align-self: stretch;

    .rc-table {
      height: auto !important;
      flex: none !important;
    }
  }

  .subject-cell--col-last {
    /* keep bottom line of the subject; overlap outer frame when flush */
    border-bottom: 1px solid $line;
    margin-bottom: -1px;
  }
}

.subject-cell {
  min-width: 0;
  /* single-sided borders only → no double vertical/horizontal layers */
  border: none;
  border-right: 1px solid $line;
  border-bottom: 1px solid $line;
  display: flex;
  flex-direction: column;
  background: #fff;
  box-sizing: border-box;
  align-self: stretch;
  height: auto;
  min-height: auto;

  .rc-table {
    flex: none;
    width: 100%;
    height: auto;
  }

  &--full {
    grid-column: 1 / -1;
    border-right: none;
  }
}

/* Outer right edge comes from parent border */
.lang-skills .subject-cell:nth-child(2n),
.subject-skills .subject-cell:nth-child(2n) {
  border-right: none;
}

/* Keep bottom line on last grid row; overlap parent edge to avoid a double stroke */
.lang-skills .subject-cell:last-child,
.subject-skills .subject-cell:last-child,
.lang-skills:not(.lang-skills--odd) .subject-cell:nth-last-child(2),
.subject-skills:not(.subject-skills--odd) .subject-cell:nth-last-child(2) {
  .rc-table tbody tr:last-child > * {
    border-bottom: 1px solid $line;
  }

  margin-bottom: -1px;
}

.rc-table {
  width: 100%;
  border-collapse: collapse;
  border-spacing: 0;
  table-layout: fixed;
  font-size: 14px;
  color: $black;
  margin: 0;

  th,
  td {
    border: none;
    border-right: 1px solid $line;
    border-bottom: 1px solid $line;
    vertical-align: middle;
    line-height: 1.35;
  }
}

.rc-table--skill,
.rc-table--subject {
  border: none;

  th,
  td {
    border: none;
    border-right: 1px solid $line;
    border-bottom: 1px solid $line;
  }

  /* last column — vertical from subject-cell / next cell */
  tr > :last-child {
    border-right: none;
  }

  /* last row border handled per layout (grid cells restore bottom line) */
  tbody tr:last-child > * {
    border-bottom: none;
  }
}

.rc-table__row--pad {
  .rc-table__criteria,
  .rc-table__mark {
    min-height: 34px;
  }
}

.rc-col-mark {
  width: 26px;
}

.rc-table__skill-title {
  text-align: center;
  font-weight: 700;
  font-size: 13.5px;
  letter-spacing: 0.02em;
  padding: 9px 5px !important;
  text-transform: uppercase;
  line-height: 1.3;
  word-wrap: break-word;
  overflow-wrap: break-word;
  hyphens: auto;
  min-height: 40px;
  vertical-align: middle;
  background: #fff;
}

.rc-table__title-hint {
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: none;
  font-size: 12px;
}

.rc-table__eu-head {
  th {
    padding: 4px 2px !important;
    font-size: 12px;
    font-weight: 700;
    text-align: center;
    background: #fff;
    height: 24px;
  }
}

.rc-table__criteria-head {
  padding: 4px 5px !important;
}

.rc-table__col-eu {
  text-align: center;
  font-weight: 700;
  font-size: 12px;
  padding: 4px 2px !important;
  width: 26px;
}

.rc-table__criteria {
  font-weight: 500;
  text-align: left;
  font-size: 13px;
  padding: 6px 7px !important;
  word-wrap: break-word;
  overflow-wrap: break-word;
  hyphens: auto;
  line-height: 1.35;
  vertical-align: middle;
}

.rc-table__mark {
  text-align: center;
  width: 26px;
  max-width: 26px;
  min-width: 26px;
  padding: 5px 2px !important;
  vertical-align: middle;
}

.rc-grade {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  font-weight: 800;
  line-height: 1;
  color: #000000;
  font-family: $report-font;
  min-width: 14px;
  text-transform: uppercase;
}

/* ── Class Teacher Comments ── */
.comments-section {
  margin-top: 10px;
  margin-bottom: 10px;
  border: none;
  page-break-inside: avoid;
}

.comments-section__title {
  text-align: center;
  font-weight: 700;
  font-size: 15px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 4px 8px 8px;
  border: none;
  color: $black;
  font-family: Georgia, "Times New Roman", serif;
  background: transparent;
}

.comments-section__body {
  position: relative;
  margin: 0;
  padding: 2px 8px 6px;
  min-height: 128px;
  background: transparent;
}

.comments-section__rules {
  position: absolute;
  left: 8px;
  right: 8px;
  top: 2px;
  z-index: 0;
  pointer-events: none;
}

.comments-section__rule {
  height: 32px;
  border: none;
  border-bottom: 1px solid #b0b0b0;
  box-sizing: border-box;
}

.comments-section__text {
  position: relative;
  z-index: 1;
  margin: 0;
  padding: 0 4px;
  min-height: 128px;
  text-align: left;
  font-size: 14px;
  font-weight: 600;
  line-height: 32px;
  color: $black;
  font-family: Georgia, "Times New Roman", serif;
  white-space: normal;
  word-break: normal;
  overflow-wrap: break-word;
  background: none;
}

/* ── Signatures ── */
.signatures {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  column-gap: 22px;
  row-gap: 10px;
  margin-top: 18px;
  padding: 8px 10px 4px;
  page-break-inside: avoid;

  &--junior {
    grid-template-columns: repeat(3, 1fr);
  }
}

.signatures__item {
  text-align: center;
  min-height: 56px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  min-width: 0;
}

.signatures__date {
  font-size: 14px;
  font-weight: 600;
  color: $black;
  margin-bottom: 4px;
  min-height: 18px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.signatures__line {
  border: none;
  border-bottom: 1px solid $line;
  width: 100%;
  height: 0;
  margin: 0 0 6px;
  display: block;
}

.signatures__label {
  font-size: 15px;
  font-weight: 600;
  color: $black;
}

@page {
  size: A4 portrait;
  margin: 3mm 4mm;
}

@media print {
  html,
  body,
  .early-year-report,
  .report-sheet,
  .report-sheet__frame,
  .report-sheet__body,
  table,
  th,
  td,
  div {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
    color-adjust: exact !important;
  }

  html,
  body {
    width: 100% !important;
    height: auto !important;
    margin: 0 !important;
    padding: 0 !important;
    background: #fff !important;
  }

  .no-print {
    display: none !important;
  }

  .early-year-report {
    background: #fff !important;
    padding: 0 !important;
    min-height: auto !important;
  }

  .report-stack {
    gap: 0 !important;
  }

  .report-sheet {
    width: 100% !important;
    max-width: 100% !important;
    min-height: 0 !important;
    height: auto !important;
    margin: 0 !important;
    page-break-after: always;
    break-after: page;
    page-break-inside: auto;

    &:last-child {
      page-break-after: auto !important;
      break-after: auto !important;
    }

    /* Square border wraps each printed page fragment */
    &__frame {
      min-height: calc(297mm - 6mm) !important;
      height: auto !important;
      max-height: none !important;
      box-shadow: none !important;
      outline: none !important;
      outline-offset: 0 !important;
      border: 1.5px solid #9a9a9a !important;
      box-sizing: border-box !important;
      overflow: visible !important;
      -webkit-box-decoration-break: clone;
      box-decoration-break: clone;
    }

    &__body {
      min-height: calc(297mm - 8mm) !important;
      height: auto !important;
      max-height: none !important;
      padding: 2.5mm 5mm 4mm !important;
      font-size: 14.5px !important;
      box-sizing: border-box !important;
      display: flex !important;
      flex-direction: column !important;
      overflow: visible !important;
    }
  }

  /* Keep small blocks together; allow large grids to fill the page */
  .letterhead,
  .student-info-table,
  .scoring-legend,
  .vision-mission,
  .lang-banner,
  .comments-section,
  .signatures {
    page-break-inside: avoid;
    break-inside: avoid;
  }

  /* Large assessment blocks MUST break, or junior pages stay half blank */
  .lang-skills,
  .subject-skills,
  .assessment-section,
  .line-block,
  .line-table {
    page-break-inside: auto !important;
    break-inside: auto !important;
  }

  /* Junior 1/2: balanced columns + allow boxes to split across pages */
  .report-sheet--junior {
    .junior-subjects,
    .junior-subjects__col,
    .assessment-section {
      page-break-inside: auto !important;
      break-inside: auto !important;
    }

    .junior-subjects {
      display: flex !important;
      align-items: stretch !important;
    }

    .junior-subjects__col {
      width: 50% !important;
      display: flex !important;
      flex-direction: column !important;
      border-right: 1px solid #8e8e8e !important;
    }

    .junior-subjects__col--right {
      border-right: none !important;
    }

    .junior-subjects .subject-cell {
      display: block !important;
      width: 100% !important;
      height: auto !important;
      flex: 0 0 auto !important;
      border: none !important;
      border-bottom: 1px solid #8e8e8e !important;
      page-break-inside: auto !important;
      break-inside: auto !important;
    }

    .junior-subjects .subject-cell--col-last {
      border-bottom: 1px solid #8e8e8e !important;
      margin-bottom: -1px !important;
    }

    .junior-subjects .subject-cell .rc-table {
      height: auto !important;
      flex: none !important;
      page-break-inside: auto !important;
      break-inside: auto !important;
    }

    .junior-subjects .subject-cell .rc-table tr {
      page-break-inside: avoid;
      break-inside: avoid;
    }

    .junior-subjects .subject-cell .rc-table__skill-title {
      page-break-after: avoid !important;
      break-after: avoid !important;
    }
  }

  .subject-cell {
    page-break-inside: avoid;
    break-inside: avoid;
  }

  .line-table__title {
    page-break-after: avoid !important;
    break-after: avoid !important;
  }

  .assessment-section {
    flex: 0 1 auto !important;
  }

  .comments-section {
    margin-top: 10px !important; /* not auto — avoids flex gap on tall pages */
  }

  .letterhead {
    margin-bottom: 5px !important;
  }

  .letterhead__logo-wrap {
    margin-bottom: 2px !important;
  }

  .letterhead__logo,
  .letterhead__logo-fallback {
    width: 88px !important;
    height: 88px !important;
  }

  .letterhead__school {
    font-size: 24px !important;
    margin-bottom: 2px !important;
  }

  .letterhead__tagline,
  .letterhead__note {
    font-size: 14px !important;
    line-height: 1.3 !important;
  }

  .letterhead__exam {
    font-size: 16px !important;
    margin-top: 4px !important;
  }

  .student-info-table__cell {
    font-size: 15px !important;
    padding: 10px 12px !important;
  }

  .student-info-table__photo {
    width: 132px !important;
    min-width: 132px !important;
    max-width: 132px !important;
    padding: 4px !important;
  }

  .student-info-table__photo-frame {
    width: 120px !important;
    height: 140px !important;
    overflow: hidden !important;
  }

  .student-info-table__img,
  .student-info-table__photo-placeholder {
    width: 100% !important;
    height: 100% !important;
    margin: 0 !important;
    position: static !important;
  }

  .student-info-table__img {
    object-fit: cover !important;
    object-position: center top !important;
    image-rendering: auto !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  .scoring-legend {
    margin-bottom: 8px !important;

    &__cell {
      width: 50% !important;
      padding: 9px 11px !important;
    }

    &__item {
      flex-direction: row !important;
      gap: 10px !important;
    }

    &__intro-cell {
      font-size: 14px !important;
    }

    &__label {
      font-size: 14.5px !important;
      line-height: 1.3 !important;
    }

    &__desc {
      font-size: 13px !important;
      line-height: 1.4 !important;
    }

    &__badge {
      width: 30px !important;
      height: 30px !important;
      font-size: 15px !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    &__head {
      background: #f7f3f0 !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    &__head-title {
      color: #8b1e2d !important;
      font-size: 15.5px !important;
    }

    &__head-sub {
      font-size: 14.5px !important;
    }

    &__table th,
    &__table td {
      border: 1px solid #8e8e8e !important;
    }

    &__badge--e {
      background: #c8e6c9 !important;
      color: #1b5e20 !important;
      border-color: #81c784 !important;
    }

    &__badge--m {
      background: #bbdefb !important;
      color: #0d47a1 !important;
      border-color: #64b5f6 !important;
    }

    &__badge--a {
      background: #ffe0b2 !important;
      color: #e65100 !important;
      border-color: #ffb74d !important;
    }

    &__badge--n {
      background: #f8d4d4 !important;
      color: #c62828 !important;
      border-color: #e8a0a0 !important;
    }
  }

  .vision-mission {
    display: grid !important;
    grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
    gap: 8px 22px !important;
    margin: 0 0 7px !important;
    padding: 7px 10px 8px !important;
    background: #fff !important;
    border: 1px solid #8e8e8e !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  .vision-mission__title {
    background: #1e3a6e !important;
    color: #fff !important;
    font-size: 13px !important;
    padding: 4px 11px !important;
    margin-bottom: 5px !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  .vision-mission__subtitle {
    font-size: 12.5px !important;
    margin-bottom: 5px !important;
    line-height: 1.25 !important;
  }

  .vision-mission__list {
    padding-left: 16px !important;
  }

  .vision-mission__list li {
    font-size: 13px !important;
    line-height: 1.3 !important;
    margin-bottom: 2px !important;
  }

  .assessment-section {
    flex: 0 1 auto !important;
  }

  .rc-table__criteria {
    font-size: 13.5px !important;
  }

  .rc-table__skill-title {
    font-size: 13.5px !important;
  }

  .rc-grade {
    font-size: 15px !important;
  }

  .line-table {
    th,
    td {
      border: 1px solid #8e8e8e !important;
      background: #fff !important;
      font-size: 16px !important;
      padding: 10px 12px !important;
    }
  }

  .line-table__title {
    font-size: 17.5px !important;
    padding: 11px 12px !important;
  }

  .line-table__title-hint {
    font-size: 15px !important;
  }

  .line-table__criteria {
    font-size: 16px !important;
  }

  .line-table__mark {
    font-size: 17px !important;
  }

  .grade-pill {
    border: 1px solid #8e8e8e !important;
    font-size: 16px !important;
    min-width: 32px !important;
    height: 28px !important;
  }

  .report-sheet--junior {
    .letterhead__school {
      font-size: 26px !important;
    }

    .letterhead__exam {
      font-size: 18px !important;
    }

    .student-info-table__cell {
      font-size: 16px !important;
      padding: 11px 12px !important;
    }

    .comments-section__title {
      font-size: 16px !important;
    }

    .comments-section__text {
      font-size: 15px !important;
      line-height: 34px !important;
    }

    .comments-section__rule {
      height: 34px !important;
    }

    .signatures__label,
    .signatures__date {
      font-size: 15px !important;
    }
  }

  .comments-section {
    margin-top: 10px !important;
    margin-bottom: 10px !important;
    border: none !important;
  }

  .comments-section__title {
    font-size: 15px !important;
    padding: 4px 8px 8px !important;
    border: none !important;
  }

  .comments-section__body {
    padding: 2px 8px 6px !important;
    min-height: 128px !important;
  }

  .comments-section__rule {
    height: 32px !important;
    border: none !important;
    border-bottom: 1px solid #8e8e8e !important;
  }

  .comments-section__text {
    font-size: 14px !important;
    line-height: 32px !important;
    padding: 0 4px !important;
    min-height: 128px !important;
    background: none !important;
  }

  .signatures {
    margin-top: 18px !important;
    padding: 8px 10px 4px !important;
    column-gap: 22px !important;
  }

  .signatures__line {
    border: none !important;
    border-bottom: 1px solid #8e8e8e !important;
    width: 100% !important;
  }

  .signatures__label,
  .signatures__date {
    font-size: 14px !important;
  }

  /*
   * Print: single-layer lines that still render in Chrome.
   * - Column/row splits: subject-cell right+bottom only (not full 4-side)
   * - Inner table: collapse + full borders (shared = 1 line), outer sides
   *   cleared so they don't stack on the subject-cell edge
   */
  $print-line: #8e8e8e;

  .lang-banner {
    border: 1px solid $print-line !important;
  }

  .lang-skills,
  .subject-skills {
    border: 1px solid $print-line !important;
    border-top: none !important;
    gap: 0 !important;
    align-items: stretch !important;
    background: #fff !important;
  }

  .junior-subjects {
    border: 1px solid $print-line !important;
    display: flex !important;
    align-items: stretch !important;
  }

  .junior-subjects__col {
    width: 50% !important;
    border-right: 1px solid $print-line !important;
    display: flex !important;
    flex-direction: column !important;
  }

  .junior-subjects__col--right {
    border-right: none !important;
  }

  .junior-subjects .subject-cell {
    border: none !important;
    border-right: none !important;
    border-bottom: 1px solid $print-line !important;
    min-height: 0 !important;
    height: auto !important;
    align-self: stretch !important;
  }

  .junior-subjects .subject-cell--col-last {
    border-bottom: 1px solid $print-line !important;
    margin-bottom: -1px !important;
  }

  .subject-skills {
    margin-top: 0 !important;
  }

  .lang-skills .subject-cell,
  .subject-skills .subject-cell {
    border: none !important;
    border-right: 1px solid $print-line !important;
    border-bottom: 1px solid $print-line !important;
    margin: 0 !important;
    background: #fff !important;
    box-shadow: none !important;
    align-self: stretch !important;
    min-height: 100% !important;
    height: auto !important;
  }

  .subject-cell--full {
    grid-column: 1 / -1 !important;
    border-right: none !important;
  }

  .lang-skills .subject-cell:nth-child(2n),
  .subject-skills .subject-cell:nth-child(2n) {
    border-right: none !important;
  }

  .lang-skills .subject-cell:last-child,
  .subject-skills .subject-cell:last-child,
  .lang-skills:not(.lang-skills--odd) .subject-cell:nth-last-child(2),
  .subject-skills:not(.subject-skills--odd) .subject-cell:nth-last-child(2) {
    border-bottom: 1px solid $print-line !important;
    margin-bottom: -1px !important;
  }

  .rc-table,
  .rc-table--skill,
  .rc-table--subject {
    border-collapse: collapse !important;
    border-spacing: 0 !important;
    border: none !important;
    width: 100% !important;
    background: #fff !important;
  }

  /* Full cell borders — collapse merges neighbors into ONE line */
  .rc-table th,
  .rc-table td,
  .rc-table--skill th,
  .rc-table--skill td,
  .rc-table--subject th,
  .rc-table--subject td {
    border: 1px solid $print-line !important;
    box-shadow: none !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  /* Clear table outer edge so it doesn't double with .subject-cell / parent */
  .subject-cell .rc-table tr > :first-child {
    border-left: none !important;
  }

  .subject-cell .rc-table tr > :last-child {
    border-right: none !important;
  }

  .subject-cell .rc-table thead tr:first-child > * {
    border-top: none !important;
  }

  .subject-cell .rc-table tbody tr:last-child > * {
    border-bottom: none !important;
  }

  .line-table,
  .scoring-legend__table,
  .student-info-table {
    border-collapse: collapse !important;
    border-spacing: 0 !important;
  }

  .line-table th,
  .line-table td,
  .scoring-legend__table th,
  .scoring-legend__table td,
  .student-info-table td {
    border: 1px solid $print-line !important;
    box-shadow: none !important;
  }

  .report-sheet__frame {
    border: 1.5px solid #9a9a9a !important;
  }

  .comments-section__rule,
  .signatures__line {
    border: none !important;
    border-bottom: 1px solid $print-line !important;
    box-shadow: none !important;
  }

  .grade-pill {
    border: 1px solid $print-line !important;
  }
}
</style>
